import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { getPlanUrl } from '@domainUtils'
import { AccessTime, Download, Transform, Warning } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Tabs,
  Tab,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { Info, TooltipIconButton } from '@ui/feedback'
import ErrorFieldBase from '@ui/forms/ErrorFieldBase'
import { MinimizeTabsWrapper } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { PDFAConformance, PDFAConformanceRecord } from '@editorComponents'

interface Props {
  plan: Plan
  /** E.g. while the plan has no PDF in storage to convert. */
  disabled?: boolean
  getBlobPDFA: (
    pdfUlr: string,
    conformance: PDFAConformance,
  ) => Promise<{ blob: Blob; errors: string[] } | null>
}

const ConvertButton = ({ plan, disabled = false, getBlobPDFA }: Props) => {
  const { projectId } = useParams()
  const project = useModelStore(state => state.project)

  const [open, setOpen] = useState(false)
  const [convertedPdf, setConvertedPdf] = useState<{ urls: string; errors: string[] } | null>(null)
  const [tab, setTab] = useState(0)

  // Add state for conformance, default to 2b
  const [conformance, setConformance] = useState(PDFAConformance.e_Level2B) // fallback to 2 if 2b not found

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const key = `pdfa-conversion-${plan.guid}`

  const planUrl = getPlanUrl(plan, projectId as string)

  const { mutate, isLoading } = useMutation(
    async () => {
      const blob = await getBlobPDFA(planUrl, conformance)
      if (!blob) throw new Error('Conversion failed')
      const objectUrl = URL.createObjectURL(blob.blob)
      setConvertedPdf({ urls: objectUrl, errors: blob.errors })
      return objectUrl
    },
    {
      onSuccess: () => {
        if (!open) {
          enqueueSnackbar(`PDF '${plan.node_name}' wurde zu PDF/A konvertiert`, {
            key,
            variant: 'success',
            action: () => (
              <Button
                color="inherit"
                onClick={() => {
                  setOpen(true)
                  closeSnackbar(key)
                }}
              >
                Öffnen
              </Button>
            ),
          })
        }
      },
      onError: () => {
        enqueueSnackbar(`PDF '${plan.node_name}' konnte nicht zu PDF/A konvertiert werden`, {
          key,
          variant: 'error',
          action: () => (
            <Button
              color="inherit"
              onClick={() => {
                setOpen(true)
                closeSnackbar(key)
              }}
            >
              Öffnen
            </Button>
          ),
        })
      },
    },
  )

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation()
    setOpen(true)
    setConvertedPdf(null)
    setTab(0)
  }

  const handleClose = () => {
    setOpen(false)
    setTab(0)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  return (
    <>
      <TooltipIconButton
        tooltip="PDF zu PDF/A konvertieren"
        disabled={disabled}
        onClick={handleOpen}
      >
        <Transform fontSize="small" />
      </TooltipIconButton>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>PDF/A konvertieren</DialogTitle>
        <DialogContent>
          {convertedPdf ? (
            <MinimizeTabsWrapper>
              <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 2 }}>
                <Tab label="Konvertierung" />
                <Tab label={`Fehler (${convertedPdf.errors.length})`} />
              </Tabs>
            </MinimizeTabsWrapper>
          ) : null}
          <Box minHeight={300}>
            {(!convertedPdf || tab === 0) && (
              <>
                {!convertedPdf && (
                  <Stack spacing={2}>
                    {isLoading ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        minHeight={80}
                      >
                        <Info icon={<AccessTime />}>
                          Sie können das Fenster schließen, wir werden Sie benachrichtigen, sobald
                          die Konvertierung abgeschlossen ist.
                        </Info>
                      </Box>
                    ) : (
                      <Box mt={2}>
                        Klicken Sie auf Konvertieren, um das PDF in PDF/A zu konvertieren.
                      </Box>
                    )}
                    <Stack direction="row" justifyContent="center">
                      <FormControl size="small" fullWidth>
                        <InputLabel id="pdfa-conformance-label">PDF/A Level</InputLabel>
                        <Select
                          labelId="pdfa-conformance-label"
                          id="pdfa-conformance"
                          value={conformance}
                          label="PDF/A Level"
                          onChange={e => setConformance(Number(e.target.value))}
                          disabled={isLoading}
                        >
                          {Object.entries(PDFAConformanceRecord).map(([key, label]) => (
                            <MenuItem key={key} value={Number(key)}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" justifyContent="center">
                      <LoadingButton
                        loading={isLoading}
                        onClick={() => mutate()}
                        color="primary"
                        variant="contained"
                      >
                        Konvertieren
                      </LoadingButton>
                    </Stack>
                  </Stack>
                )}
                {convertedPdf && tab === 0 && (
                  <Box mt={2}>
                    {convertedPdf.errors.length > 0 && (
                      <Alert icon={<Warning />} color="warning">
                        Die PDF wurde zu PDF/A konvertiert, aber es sind Fehler aufgetreten. In den
                        allermeisten Fällen war die Konvertierung erfolgreich und es wurden nur
                        inkompatible Elemente weggelassen.
                      </Alert>
                    )}
                    <Stack direction="row" spacing={1} mt={2} justifyContent="center">
                      <Button
                        href={convertedPdf.urls}
                        target="_blank"
                        rel="noopener"
                        variant="contained"
                        color="primary"
                        endIcon={<Download />}
                        download={`${plan.node_name}-${project?.name}-${PDFAConformanceRecord[conformance]}.pdf`}
                      >
                        PDF/A herunterladen
                      </Button>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={2} justifyContent="center">
                      <Button
                        onClick={() => {
                          setConvertedPdf(null)
                          setTab(0)
                        }}
                        variant="outlined"
                        color="primary"
                      >
                        Neu Konvertieren
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            )}
            {convertedPdf && tab === 1 && (
              <Stack direction="column" spacing={1} mt={2} sx={{ overflowY: 'auto' }}>
                {convertedPdf?.errors.length ? (
                  convertedPdf.errors.map((error, index) => (
                    <ErrorFieldBase key={index} severity="error" message={error} />
                  ))
                ) : (
                  <Box>Keine Fehler vorhanden.</Box>
                )}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ConvertButton
